/**
 * Sanitising of user-provided text (player names, lobby names, chat).
 *
 * Used by the multiplayer server (authoritative) and usable by clients (preview /
 * singleplayer). All functions are pure and idempotent:
 *   sanitizeName(sanitizeName(x)) === sanitizeName(x)
 *
 * What is done:
 *   - NFC normalisation
 *   - removal of control characters, invisible formatting characters (zero-width,
 *     bidi overrides, Hangul fillers, tag characters, ...)
 *   - limiting runs of combining marks ("zalgo" text)
 *   - collapsing all whitespace to single spaces + trimming
 *   - length limits measured in grapheme clusters (emoji sequences are never split)
 *   - masking a very small list of slurs / profanity (EN + NL) with asterisks
 */

export const MAX_NAME_LENGTH = 24;
export const MAX_LOBBY_NAME_LENGTH = 40;
export const MAX_CHAT_LENGTH = 240;
export const DEFAULT_PLAYER_NAME = "Player";

/** Hard cap on UTF-16 code units for any sanitised string (protects against huge graphemes). */
const MAX_CODE_UNITS_PER_GRAPHEME = 8;

/**
 * Invisible / dangerous characters that are removed entirely:
 * C0 + C1 controls (except whitespace, handled separately), soft hyphen, combining grapheme joiner,
 * Arabic letter mark, Hangul fillers, Khmer invisible vowels, Mongolian vowel separator,
 * zero-width space/non-joiner, LRM/RLM, bidi embeddings/overrides/isolates, word joiner and
 * invisible operators, deprecated format chars, BOM, interlinear annotation chars, tag characters.
 * ZWJ (U+200D) and variation selectors are kept because emoji sequences need them.
 */
const STRIP_RE =
  /[\u0000-\u0008\u000E-\u001F\u007F-\u009F­͏؜ᅟᅠ឴឵᠎​‌‎‏‪-‮⁠-⁤⁦-⁯ㅤ﻿ﾠ￹-￻]|[\u{E0000}-\u{E007F}]/gu;

/** More than two consecutive combining marks are cut down to two. */
const COMBINING_RUN_RE = /(\p{M}{2})\p{M}+/gu;

const WHITESPACE_RE = /\s+/gu;

/** Leading/trailing ZWJ have no meaning and would render as nothing. */
const EDGE_ZWJ_RE = /^‍+|‍+$/g;

let segmenter: Intl.Segmenter | null | undefined;

function graphemes(s: string): string[] {
  if (segmenter === undefined) {
    try {
      segmenter = typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter(undefined, { granularity: "grapheme" }) : null;
    } catch {
      segmenter = null;
    }
  }
  if (segmenter) {
    const out: string[] = [];
    for (const seg of segmenter.segment(s)) out.push(seg.segment);
    return out;
  }
  return Array.from(s);
}

/** Truncate to at most `max` grapheme clusters (and a proportional code-unit budget). */
export function truncateGraphemes(s: string, max: number): string {
  if (max <= 0) return "";
  // Fast path: pure short strings.
  if (s.length <= max) return s;
  const parts = graphemes(s);
  const budget = max * MAX_CODE_UNITS_PER_GRAPHEME;
  let out = "";
  let count = 0;
  for (const g of parts) {
    if (count >= max || out.length + g.length > budget) break;
    out += g;
    count++;
  }
  return out;
}

/** Number of grapheme clusters in a string. */
export function graphemeLength(s: string): number {
  return graphemes(s).length;
}

/** Shared cleaning pipeline (without length limit / profanity). */
export function cleanText(raw: unknown): string {
  if (typeof raw !== "string") return "";
  // Bound the work done on hostile input before running regexes over it.
  let s = raw.length > 4096 ? raw.slice(0, 4096) : raw;
  try {
    s = s.normalize("NFC");
  } catch {
    // Lone surrogates can make normalize throw in some engines; fall through with the raw string.
  }
  s = s.replace(STRIP_RE, "");
  // Remove lone surrogates (invalid UTF-16) which break JSON consumers / fonts.
  s = s.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "");
  s = s.replace(COMBINING_RUN_RE, "$1");
  s = s.replace(WHITESPACE_RE, " ").trim();
  s = s.replace(EDGE_ZWJ_RE, "").trim();
  return s;
}

// ---------------------------------------------------------------------------
// Profanity masking
// ---------------------------------------------------------------------------

/**
 * Roots matched anywhere inside a word (after normalisation). Only roots that are
 * (practically) never part of innocent words belong here.
 */
const SUBSTRING_ROOTS = ["fuck", "nigger", "nigga", "cunt", "kanker", "faggot", "hitler", "motherfuck", "whore"];

/**
 * Roots matched only as a whole word or with a common suffix (to avoid the
 * "Scunthorpe problem": cockpit, grape, catering, ...).
 */
const WORD_ROOTS = [
  // English
  "shit",
  "bullshit",
  "bitch",
  "slut",
  "fag",
  "retard",
  "asshole",
  "dickhead",
  "cock",
  "pussy",
  "bastard",
  "wanker",
  "twat",
  "rape",
  "rapist",
  "nazi",
  "kys",
  // Dutch
  "kut",
  "tering",
  "tyfus",
  "hoer",
  "klootzak",
  "lul",
  "mongool",
  "debiel",
  "godverdomme",
  "flikker",
  "neger",
  "kankerlijer",
];

const ALLOWED_SUFFIXES = ["", "s", "es", "er", "ers", "ing", "in", "ed", "y", "ie", "ies", "en", "je", "jes", "z", "zz", "o"];

const LEET: Record<string, string> = {
  "0": "o",
  "1": "i",
  "!": "i",
  "|": "i",
  "3": "e",
  "4": "a",
  "@": "a",
  "5": "s",
  $: "s",
  "7": "t",
  "8": "b",
  "9": "g",
  "+": "t",
};

/** Collapse runs of the same letter ("fuuuck" -> "fuck", "asshole" -> "ashole"). */
function collapseRepeats(s: string): string {
  let out = "";
  let prev = "";
  for (const ch of s) {
    if (ch !== prev) out += ch;
    prev = ch;
  }
  return out;
}

function normalizeToken(token: string): string {
  let t: string;
  try {
    t = token.normalize("NFKD");
  } catch {
    t = token;
  }
  t = t.replace(/\p{M}/gu, "").toLowerCase();
  let out = "";
  for (const ch of t) out += LEET[ch] ?? ch;
  out = out.replace(/[^a-z]/g, "");
  return collapseRepeats(out);
}

const SUBSTRING_NORMALIZED = SUBSTRING_ROOTS.map(collapseRepeats);
const WORD_NORMALIZED = WORD_ROOTS.map(collapseRepeats);

function isProfane(token: string): boolean {
  const n = normalizeToken(token);
  if (n.length < 3) return false;
  for (const root of SUBSTRING_NORMALIZED) if (n.includes(root)) return true;
  for (const root of WORD_NORMALIZED) {
    if (!n.startsWith(root)) continue;
    const rest = n.slice(root.length);
    if (ALLOWED_SUFFIXES.includes(rest)) return true;
  }
  return false;
}

/** Word-ish tokens including leetspeak characters. */
const TOKEN_RE = /[\p{L}\p{N}@$!|+]+/gu;

/** Replace profane words with asterisks of the same length. */
export function maskProfanity(text: string): string {
  return text.replace(TOKEN_RE, (token) => (isProfane(token) ? "*".repeat(Array.from(token).length) : token));
}

/** True when the text contains a word from the mask list. */
export function containsProfanity(text: string): boolean {
  const matches = text.match(TOKEN_RE);
  if (!matches) return false;
  return matches.some(isProfane);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sanitise a player name. Returns `fallback` when nothing usable remains
 * (empty / only invisible chars / only masked characters).
 */
export function sanitizeName(raw: unknown, fallback: string = DEFAULT_PLAYER_NAME, maxLength: number = MAX_NAME_LENGTH): string {
  let s = cleanText(raw);
  s = truncateGraphemes(s, maxLength).trim();
  s = maskProfanity(s);
  if (s.length === 0 || /^[\s*]+$/u.test(s)) return fallback;
  return s;
}

/** Sanitise a lobby name (slightly longer limit than player names). */
export function sanitizeLobbyName(raw: unknown, fallback: string): string {
  return sanitizeName(raw, fallback, MAX_LOBBY_NAME_LENGTH);
}

/** Sanitise a chat line. Returns "" when nothing usable remains (caller should drop it). */
export function sanitizeChat(raw: unknown, maxLength: number = MAX_CHAT_LENGTH): string {
  let s = cleanText(raw);
  s = truncateGraphemes(s, maxLength).trim();
  s = maskProfanity(s);
  return s;
}

/**
 * Sanitise a flag value: an ISO-3166 alpha-2 code (case-insensitive, returned lowercase),
 * a short identifier like "eu" / "un" / "pirate", or a single emoji grapheme.
 * Returns undefined when the input is not acceptable.
 */
export function sanitizeFlag(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const s = cleanText(raw);
  if (s.length === 0) return undefined;
  if (/^[A-Za-z0-9_-]{1,16}$/.test(s)) return s.toLowerCase();
  // Single emoji / pictographic grapheme (e.g. a regional-indicator flag pair).
  const g = graphemes(s);
  if (g.length === 1 && g[0].length <= 16 && /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(g[0])) return g[0];
  return undefined;
}
